import { call, put } from 'redux-saga/effects';
import { History } from 'history';
import { v4 as generateId } from 'uuid';
import i18next from 'i18next';

import { AuthAPI } from 'services/auth.api';
import {
  login,
  signup,
  forgotPassword,
  resetPassword,
  createNewPassword,
  checkAuth,
  checkPasswordResetToken,
  setAuthLoading,
  setCheckPasswordResetTokenLoading,
  setLoginLoading,
  checkSsoAzureConfig,
  checkSsoJwtConfig,
  checkSsoSamlConfig,
} from 'actions/app.actions';
import { enqueueSnackbar } from 'actions/notifications.actions';
import { setAuthToken } from 'services/api';
import { getProvidedByServerErrors } from 'core/utils/api.helpers';
import { ActionType } from 'interfaces';
import { ROUTES } from '../constants';

// TODO: fix Saga types
interface ICallback {
  (): void;
}
type LoginSagaType = {
  payload: Record<
    'username' | 'password' | 'history' | 'callback',
    string | History | ICallback
  >;
};

export function* loginSaga({
  payload: { username, password, callback },
}: ActionType): any {
  try {
    yield put(setLoginLoading(true));
    const {
      data: { token },
    } = yield call(AuthAPI.login, { username, password });
    yield setAuthToken(token);
    yield put(login.success(token));
    yield put(checkAuth.request(token));
    yield callback(token);
    yield put(setLoginLoading(false));
  } catch (error) {
    yield put(setLoginLoading(false));
    const providedByServerErrors = getProvidedByServerErrors(error);
    const key = yield call(generateId);
    yield put(
      enqueueSnackbar({
        message: providedByServerErrors || i18next.t('auth.login.error'),
        options: {
          key,
          variant: 'error',
        },
      }),
    );
    yield put(login.failure({ error, message: 'error.fail' }));
  }
}

export function* signUpSaga({ payload: { data, onSignUp } }: ActionType): any {
  try {
    yield put(setLoginLoading(true));

    const {
      data: { token },
    } = yield call(AuthAPI.signUp, data);
    yield onSignUp(token);
    yield put(signup.success());
    const key = yield call(generateId);
    yield put(
      enqueueSnackbar({
        message: i18next.t('auth.signup.success'),
        options: {
          key,
          variant: 'success',
        },
      }),
    );
  } catch (error) {
    const providedByServerErrors = getProvidedByServerErrors(error);
    const key = yield call(generateId);
    yield put(
      enqueueSnackbar({
        message: providedByServerErrors || i18next.t('auth.signup.error'),
        options: {
          key,
          variant: 'error',
        },
      }),
    );
    yield put(signup.failure({ error, message: 'error.fail' }));
  } finally {
    yield put(setLoginLoading(false));
  }
}

export function* forgotPasswordSaga({ payload: { email } }: ActionType): any {
  try {
    yield put(setLoginLoading(true));
    yield call(AuthAPI.forgotPassword, email);
    yield put(forgotPassword.success());
    yield put(setLoginLoading(false));
  } catch (error) {
    yield put(setLoginLoading(false));
    yield put(forgotPassword.failure({ error, message: 'error.fail' }));
  } finally {
    const key = yield call(generateId);

    yield put(
      enqueueSnackbar({
        message: i18next.t('auth.reset_password.email_sent'),
        options: {
          key,
          variant: 'info',
        },
      }),
    );
  }
}

export function* resetPasswordSaga({
  payload: { history, ...data },
}: ActionType): any {
  try {
    yield call(AuthAPI.resetPassword, data);
    const key = yield call(generateId);
    yield put(
      enqueueSnackbar({
        message: i18next.t('auth.reset_password.success'),
        options: {
          key,
          variant: 'success',
        },
      }),
    );
    yield call(history.push, ROUTES.LOGIN);
  } catch (error) {
    const key = yield call(generateId);
    yield put(
      enqueueSnackbar({
        message: i18next.t('auth.signup.error'),
        options: {
          key,
          variant: 'error',
        },
      }),
    );
    yield put(resetPassword.failure({ error, message: 'error.fail' }));
  }
}

export function* createNewPasswordSaga({
  payload: { onCreate, ...data },
}: ActionType): any {
  try {
    yield put(setLoginLoading(true));
    const {
      data: { token },
    } = yield call(AuthAPI.createNewPassword, data);
    yield call(onCreate, token);
    yield put(createNewPassword.success());
    const key = yield call(generateId);
    yield put(
      enqueueSnackbar({
        message: i18next.t('auth.createNewPassword.success'),
        options: {
          key,
          variant: 'success',
        },
      }),
    );
  } catch (error) {
    yield put(createNewPassword.failure({ error, message: 'error.fail' }));
    const key = yield call(generateId);
    yield put(
      enqueueSnackbar({
        message: i18next.t('auth.createNewPassword.error'),
        options: {
          key,
          variant: 'error',
        },
      }),
    );
  } finally {
    yield put(setLoginLoading(false));
  }
}

export function* checkAuthenticationSaga({
  payload: { token },
}: ActionType): any {
  try {
    yield put(setAuthLoading(true));
    const { data: userInfos } = yield call(AuthAPI.checkAuth);
    yield put(checkAuth.success(token, userInfos));
    yield put(setAuthLoading(false));
  } catch (e) {
    yield put(setAuthLoading(false));
  }
}

export function* checkSsoAzureConfigSaga(): any {
  try {
    const { data: isSsoAzureConfigured } = yield call(
      AuthAPI.checkSsoAzureConfig,
    );
    yield put(checkSsoAzureConfig.success(isSsoAzureConfigured));
  } catch (error) {
    yield put(checkSsoAzureConfig.failure(error));
  }
}

export function* checkSsoJwtConfigSaga(): any {
  try {
    const { data: isSsoJwtConfigured } = yield call(AuthAPI.checkSsoJwtConfig);
    yield put(checkSsoJwtConfig.success(isSsoJwtConfigured));
  } catch (error) {
    yield put(checkSsoJwtConfig.failure(error));
  }
}

export function* checkSsoSamlConfigSaga(): any {
  try {
    const { data: isSsoSamlConfigured } = yield call(AuthAPI.checkSsoSamlConfig);
    yield put(checkSsoSamlConfig.success(isSsoSamlConfigured));
  } catch (error) {
    yield put(checkSsoSamlConfig.failure(error));
  }
}

export function* checkPasswordResetTokenSaga({
  payload: { token },
}: ActionType): any {
  try {
    yield put(setCheckPasswordResetTokenLoading(true));
    const {
      data: { isValid, email },
    } = yield call(AuthAPI.checkPasswordResetToken, token);
    yield put(checkPasswordResetToken.success({ isValid, email }));
  } catch (error) {
    yield put(checkPasswordResetToken.failure(error));
  } finally {
    yield put(setCheckPasswordResetTokenLoading(false));
  }
}
